import { fraunces, readingSerif } from '../Landing/fonts';

// Field-note prose: the reading typography of the post page, without its
// promo/width CSS. Used for guide bodies ("What's unique?", markdown mini
// guides) and the AI live guide, whose streaming state gets a status chip,
// shimmer placeholder and typing caret.
export default function FieldNotesProse({
  children,
  dropCap,
  centered,
  live,
  isStreaming,
  hasBody,
}) {
  return (
    <div className={centered ? 'tw-mx-auto tw-max-w-[46rem]' : ''}>
      {live && (
        <p className="tw-mb-6 tw-mt-0">
          <span className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-border tw-border-solid tw-border-primary-600/30 dark:tw-border-primary-300/30 tw-bg-primary-600/[0.07] dark:tw-bg-primary-300/10 tw-px-3.5 tw-py-1.5 tw-font-mono tw-text-[10px] tw-font-semibold tw-tracking-[0.25em] tw-text-primary-700 dark:tw-text-primary-300">
            {isStreaming || !hasBody ? (
              <>
                <span
                  aria-hidden="true"
                  className="tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-primary-600 dark:tw-bg-primary-300 tw-animate-pulse motion-reduce:tw-animate-none"
                />
                LIVE · FIELD NOTES IN PROGRESS
              </>
            ) : (
              <>FILED · JUST NOW</>
            )}
          </span>
        </p>
      )}

      {live && !hasBody ? (
        <div aria-hidden="true">
          <div className="tw-h-3.5 tw-w-full tw-animate-pulse tw-rounded tw-bg-[#EDE7D8] dark:tw-bg-[#26382F]" />
          <div className="tw-mt-3 tw-h-3.5 tw-w-4/5 tw-animate-pulse tw-rounded tw-bg-[#EDE7D8] dark:tw-bg-[#26382F]" />
          <div className="tw-mt-3 tw-h-3.5 tw-w-3/5 tw-animate-pulse tw-rounded tw-bg-[#EDE7D8] dark:tw-bg-[#26382F]" />
        </div>
      ) : (
        <div
          className={`tf-prose ${dropCap ? 'tf-prose-dropcap' : ''} ${
            live && isStreaming ? 'tf-prose-caret' : ''
          }`}
        >
          {children}
        </div>
      )}

      <style jsx global>{`
        .tf-prose {
          font-family: ${readingSerif.style.fontFamily};
          font-size: 1.0625rem;
          line-height: 1.75;
          color: #243630;
        }
        .tw-dark .tf-prose {
          color: #d3dad2;
        }
        @media (min-width: 768px) {
          .tf-prose {
            font-size: 1.125rem;
          }
        }
        .tf-prose p {
          margin: 0 0 1.1em;
        }
        .tf-prose h1,
        .tf-prose h2,
        .tf-prose h3,
        .tf-prose h4 {
          font-family: ${fraunces.style.fontFamily};
          font-weight: 600;
          letter-spacing: -0.01em;
          line-height: 1.22;
          color: #1d2d28;
          margin: 1.6em 0 0.6em;
        }
        .tw-dark .tf-prose h1,
        .tw-dark .tf-prose h2,
        .tw-dark .tf-prose h3,
        .tw-dark .tf-prose h4 {
          color: #ebe6d9;
        }
        .tf-prose h1 {
          font-size: 1.8em;
        }
        .tf-prose h2 {
          font-size: 1.45em;
        }
        .tf-prose h3 {
          font-size: 1.2em;
        }
        /* Same link treatment as the post body (.postcontent p a in
           style.css): teal with a gradient that fills on hover — dark ink
           on the teal fill stays readable in both themes. */
        .tf-prose a {
          color: #00796b;
          text-decoration: none;
          border-bottom: 2px solid #4db6ac;
          background-size: 100% 200%;
          background-image: linear-gradient(to top, #4db6ac 50%, transparent 50%);
          transition: background-position 0.2s;
        }
        .tw-dark .tf-prose a {
          color: #80cbc4;
        }
        .tf-prose a:hover,
        .tw-dark .tf-prose a:hover {
          color: #1f1f1f;
          background-position: 0 100%;
        }
        .tf-prose ul,
        .tf-prose ol {
          margin: 0 0 1.1em;
          padding-left: 1.4em;
        }
        .tf-prose li {
          margin-bottom: 0.35em;
        }
        .tf-prose-dropcap p:first-of-type::first-letter {
          font-family: ${fraunces.style.fontFamily};
          font-weight: 600;
          font-size: 3.1em;
          line-height: 0.85;
          float: left;
          padding-right: 0.12em;
          padding-top: 0.05em;
          color: #00796b;
        }
        .tw-dark .tf-prose-dropcap p:first-of-type::first-letter {
          color: #80cbc4;
        }
        .tf-prose-caret > *:last-child::after {
          content: '';
          display: inline-block;
          width: 2px;
          height: 1em;
          margin-left: 2px;
          vertical-align: text-bottom;
          background: #00897b;
          animation: tf-caret-blink 1s steps(2) infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .tf-prose-caret > *:last-child::after {
            animation: none;
          }
        }
        @keyframes tf-caret-blink {
          0% {
            opacity: 1;
          }
          50% {
            opacity: 0;
          }
          100% {
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}
